/**
 * Workforce Explorer — the Remco establishment pack as a live screen.
 *
 * Pick a basis and a point in time; every figure recomputes from the same
 * point-in-time engine that built the board pack. Three things this page takes
 * seriously, because a board-facing number that is quietly wrong is worse than
 * no number at all:
 *
 *  1. BASIS IS EXPLICIT. "How many people work here" has three defensible
 *     answers. There is no default on the API and none invented here.
 *  2. EVERY PANEL STATES ITS OWN WINDOW. Turnover is a financial year, span is
 *     point-in-time, payroll and the student ratio come from outside sources and
 *     move with neither control. Panels say so on their face rather than looking
 *     as though they followed the picker.
 *  3. "TODAY" IS THE LAST SYNC, NOT REAL TIME. The live chip carries the sync
 *     stamp and never claims more than that.
 *
 * Loading is Promise.allSettled per group, so a single dead endpoint degrades
 * one panel instead of blanking the screen. Every loader is gated on the same
 * permission as the surface it feeds — apiClient raises a global 403 toast that
 * a call site cannot suppress, so a request that will 403 must never be fired.
 *
 * Endpoints (the only ones this page calls):
 *   GET /api/v1/identity/org/workforce/periods
 *   GET /api/v1/identity/org/workforce/summary?basis&as_at
 *   GET /api/v1/identity/org/workforce/trend?basis
 *   GET /api/v1/identity/org/workforce/breakdown?basis&as_at&group_by
 *   GET /api/v1/identity/org/workforce/panels?basis&as_at
 *   GET /api/v1/identity/org/workforce/metric-definitions
 *   GET /api/v1/identity/org/workforce/export.xlsx?basis&as_at   (export grant only)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Download, History, Info, Loader2, RefreshCw, ShieldAlert } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useAuth } from '@/providers/AuthProvider';
import { hasAnyPermission, hasPermission, hasRole } from '@/auth/permissions';
import {
  WORKFORCE_KEYS,
  WORKFORCE_PERMISSION_KEYS,
  WORKFORCE_ROLES,
  WORKFORCE_SYNC_ROLES,
} from '@/lib/workforcePermissions';

import { BasisToggle } from './components/BasisToggle';
import { BreakdownPanel } from './components/BreakdownPanel';
import { CompositionPanel } from './components/CompositionPanel';
import { MetricTile, TileRow } from './components/MetricTile';
import { PayrollPanel } from './components/PayrollPanel';
import { PeriodChips } from './components/PeriodChips';
import { AdoptionPanel } from './components/AdoptionPanel';
import { AgeServicePanel } from './components/AgeServicePanel';
import { CampusFootprintPanel } from './components/CampusFootprintPanel';
import { EquityPanel } from './components/EquityPanel';
import { ExitRolesPanel } from './components/ExitRolesPanel';
import { IcaGovernancePanel } from './components/IcaGovernancePanel';
import { IndicatorsPanel } from './components/IndicatorsPanel';
import { KeyPersonPanel } from './components/KeyPersonPanel';
import { MobilityPanel } from './components/MobilityPanel';
import { RangeSlicer } from './components/RangeSlicer';
import { RoleCataloguePanel } from './components/RoleCataloguePanel';
import { RoleContractMixPanel } from './components/RoleContractMixPanel';
import { SessionalPanel } from './components/SessionalPanel';
import { StaffCostPanel } from './components/StaffCostPanel';
import { VacancyStepDownPanel } from './components/VacancyStepDownPanel';
import { RatioPanel } from './components/RatioPanel';
import { SpanPanel } from './components/SpanPanel';
import { TrendPanel } from './components/TrendPanel';
import { TurnoverPanel } from './components/TurnoverPanel';
import {
  basisLabel,
  buildDefLookup,
  Def,
  fmtDay,
  fmtNum,
  fmtSyncStamp,
  workforceApi,
  type MetricDefinition,
  type WorkforceBasis,
  type WorkforceBreakdown,
  type WorkforceGroupBy,
  type WorkforcePanels,
  type WorkforcePeriod,
  type WorkforceSummary,
  type WorkforceTrend,
} from './components/apiContract';

/** Default to the last historical snapshot (the reporting cutoff), not the live
 *  frame — a board reader wants the figure that matches the pack by default. */
function defaultPeriod(list: WorkforcePeriod[]): string | null {
  if (!list.length) return null;
  const historical = list.filter((p) => !p.is_live);
  return (historical.length ? historical[historical.length - 1] : list[list.length - 1]).as_at;
}

/** Human labels for the published-vs-rebuilt rows. Keyed on the dotted paths the
 *  producer emits, so an unmapped measure still renders (as its raw key) rather
 *  than vanishing from a reconciliation table. */
const PUBLISHED_LABELS: Record<string, string> = {
  'employee_positions.filled': 'Employees in post',
  'employee_positions.approved': 'Employee seats',
  'positions.approved': 'Approved positions',
  'positions.filled': 'Filled seats',
};

/** Rows in the order above — employees first, per the house reporting rule —
 *  rather than whatever order the producer's JSON happened to serialise in.
 *  Anything unmapped is appended so a new measure still shows, just last. */
const orderedDrift = (drift: Record<string, { published: number; rebuilt: number; delta: number }>) => {
  const order = Object.keys(PUBLISHED_LABELS);
  return Object.entries(drift).sort(([a], [b]) => {
    const ia = order.indexOf(a);
    const ib = order.indexOf(b);
    return (ia < 0 ? order.length : ia) - (ib < 0 ? order.length : ib);
  });
};

/** Reader-facing names for panel blocks, for the staleness banner: one for every
 *  block identity serves, worded after the panel that shows it so a reader can find
 *  it. Two blocks share a label where one panel shows both (span on either basis;
 *  the ICA deep dive on FTE or on the older position basis). An unmapped block
 *  still appears (as its key with spaces) rather than dropping out of the list. */
const STALE_BLOCK_LABELS: Record<string, string> = {
  // establishment
  campus_footprint: 'campus footprint',
  titles_trend: 'role titles in use',
  role_catalogue: 'role catalogue',
  role_contract_mix: 'contract type mix by role',
  // structure
  span_employees: 'span of control',
  span_all: 'span of control',
  narrow_spans: 'managers with one to three reports',
  key_person: 'single-incumbent senior roles',
  // people
  equity: 'employment equity',
  age_service: 'age and length of service',
  sessional_profile: 'ICA and sessional workforce',
  // movement
  turnover: 'staff turnover',
  exit_reasons: 'exit reasons',
  conversions: 'leavers set apart',
  exit_roles: 'exits by role',
  mobility: 'internal mobility',
  // platform
  adoption: 'platform adoption',
  indicators: 'impact indicators',
  // Finance payroll months
  ica_governance_fte: 'ICA deep dive',
  ica_governance: 'ICA deep dive',
  sessional_capacity: 'sessional workers paid in the month',
  pay: 'paid headcount and FTE',
  pay_cost: 'staff cost and productivity',
  pay_reconciliation: 'Finance headcount reconciliation',
  ratio: 'students per lecturer FTE',
};

/** The blocks Finance publishes by payroll month. Identity keeps a block whose
 *  provenance says `kind: 'payroll_month'` off the stale list until a later month is
 *  overdue; these names cover a copy stored before kinds were stamped, which
 *  identity would otherwise list as stale merely for being dated at its month's end. */
const PAYROLL_MONTH_BLOCKS = new Set([
  'ica_governance_fte',
  'sessional_capacity',
  'pay',
  'pay_cost',
  'pay_reconciliation',
  'ratio',
]);
/** Identity's own rule (`_panel_provenance`): a payroll month turns stale only once a
 *  later month should have landed — more than two months (62 days) past its month. */
const PAYROLL_MONTH_STALE_AFTER_DAYS = 62;

/** The chip label for a frame — the live frame is "Today", never its date. */
const frameLabel = (p?: WorkforcePeriod | null) =>
  !p ? '—' : p.is_live ? 'Today' : p.label || fmtDay(p.as_at);

/** Filenames are UTC by house convention; anything a human reads renders SAST. */
function utcStamp(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}` +
    `_${p(d.getUTCHours())}${p(d.getUTCMinutes())}${p(d.getUTCSeconds())}`
  );
}

const WorkforceExplorerPage: React.FC = () => {
  const { user } = useAuth();

  // Mirrors the server gate exactly: the router admits either key, or any
  // ORG_CONSOLE role. Widening this would fire requests that 403; narrowing it
  // would hide the page from someone the server would let in.
  const canView = hasAnyPermission(user, WORKFORCE_PERMISSION_KEYS, WORKFORCE_ROLES);
  const canExport = hasPermission(user, WORKFORCE_KEYS.export, WORKFORCE_ROLES);
  // Republishing the frames is a ROLE decision, never a grant: reading the
  // dashboard is not authority to rewrite what it serves. Mirrors the server's
  // _REBUILD_GATE, which is the actual control.
  const canSync = hasRole(user, WORKFORCE_SYNC_ROLES);

  const [basis, setBasis] = useState<WorkforceBasis>('employees');
  const [asAt, setAsAt] = useState<string | null>(null);
  // The RANGE is a second, separate time control: it says which frames the
  // time-series chart draws. It is held as dates rather than indices so a
  // refreshed period list (a new live frame appears overnight) cannot silently
  // slide the window onto different frames.
  const [rangeFrom, setRangeFrom] = useState<string | null>(null);
  const [rangeTo, setRangeTo] = useState<string | null>(null);
  /** Said out loud whenever one control had to move the other. */
  const [pickerNote, setPickerNote] = useState<string | null>(null);
  const [groupBy, setGroupBy] = useState<WorkforceGroupBy>('function');
  const [reloadToken, setReloadToken] = useState(0);

  const [periods, setPeriods] = useState<WorkforcePeriod[]>([]);
  const [budgetAligned, setBudgetAligned] = useState<boolean | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<MetricDefinition[] | null>(null);

  const [summary, setSummary] = useState<WorkforceSummary | null>(null);
  const [trend, setTrend] = useState<WorkforceTrend | null>(null);
  const [panels, setPanels] = useState<WorkforcePanels | null>(null);
  const [breakdown, setBreakdown] = useState<WorkforceBreakdown | null>(null);

  const [booting, setBooting] = useState(true);
  const [bootFailed, setBootFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  /** On-demand rebuild. The run outlives the gateway's 300s ceiling, so the
   *  button STARTS it and this polls until it finishes, then reloads the page. */
  const [syncing, setSyncing] = useState(false);
  const [syncNote, setSyncNote] = useState<string | null>(null);

  const def = useMemo(() => buildDefLookup(metrics), [metrics]);

  // ---- boot: periods + metric definitions (once) ---------------------------
  useEffect(() => {
    if (!canView) {
      setBooting(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const [periodsRes, defsRes] = await Promise.allSettled([
        workforceApi.getPeriods(),
        workforceApi.getMetricDefinitions(),
      ]);
      if (cancelled) return;

      if (periodsRes.status === 'fulfilled') {
        const list = periodsRes.value?.periods ?? [];
        setPeriods(list);
        setBudgetAligned(periodsRes.value?.budget_aligned ?? null);
        setGeneratedAt(periodsRes.value?.generated_at ?? null);
        setAsAt((prev) => prev ?? defaultPeriod(list));
        // Full range on first load: the reader sees the whole published history
        // until they choose otherwise. A range already set survives a refresh.
        setRangeFrom((prev) => prev ?? list[0]?.as_at ?? null);
        setRangeTo((prev) => prev ?? list[list.length - 1]?.as_at ?? null);
      } else {
        console.error('Workforce Explorer: /periods failed', periodsRes.reason);
        setBootFailed(true);
      }

      if (defsRes.status === 'fulfilled') {
        setMetrics(defsRes.value?.metrics ?? []);
      } else {
        // Non-fatal: tooltips fall back to the locally-held definitions.
        console.error('Workforce Explorer: /metric-definitions failed', defsRes.reason);
        setMetrics([]);
      }
      setBooting(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [canView, reloadToken]);

  // ---- summary + trend + panels -------------------------------------------
  useEffect(() => {
    if (!canView || !asAt) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const [summaryRes, trendRes, panelsRes] = await Promise.allSettled([
        workforceApi.getSummary({ basis, asAt }),
        workforceApi.getTrend({ basis }),
        workforceApi.getPanels({ basis, asAt }),
      ]);
      if (cancelled) return;

      if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value);
      else {
        console.error('Workforce Explorer: /summary failed', summaryRes.reason);
        setSummary(null);
      }
      if (trendRes.status === 'fulfilled') setTrend(trendRes.value);
      else {
        console.error('Workforce Explorer: /trend failed', trendRes.reason);
        setTrend(null);
      }
      if (panelsRes.status === 'fulfilled') setPanels(panelsRes.value);
      else {
        console.error('Workforce Explorer: /panels failed', panelsRes.reason);
        setPanels(null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [canView, asAt, basis, reloadToken]);

  // ---- breakdown (its own axis, so its own request) ------------------------
  useEffect(() => {
    if (!canView || !asAt) return;
    let cancelled = false;
    setBreakdownLoading(true);
    (async () => {
      const [res] = await Promise.allSettled([workforceApi.getBreakdown({ basis, asAt, groupBy })]);
      if (cancelled) return;
      if (res.status === 'fulfilled') setBreakdown(res.value);
      else {
        console.error('Workforce Explorer: /breakdown failed', res.reason);
        setBreakdown(null);
      }
      setBreakdownLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [canView, asAt, basis, groupBy, reloadToken]);

  // ---- export --------------------------------------------------------------
  const doExport = useCallback(async () => {
    if (!canExport || !asAt) return;
    setExporting(true);
    // Composed client-side on purpose: the gateway sets no expose_headers, so
    // Content-Disposition is unreadable from the browser in PROD and every
    // "read the filename off the header" site silently takes its fallback.
    const filename = `Eduvos_Workforce_Explorer_${basis}_${asAt}_${utcStamp(new Date())}.xlsx`;
    try {
      const blob = await workforceApi.downloadWorkbook({ basis, asAt });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Workbook downloaded — ${filename}`);
    } catch (e) {
      console.error('Workforce Explorer export failed', e);
      toast.error('Export failed — nothing was downloaded. Try again, or refresh the page.');
    } finally {
      setExporting(false);
    }
  }, [canExport, asAt, basis]);

  // ---- on-demand sync ------------------------------------------------------
  /**
   * Rebuild the frames now, rather than waiting for the 07:00 SAST job.
   *
   * The rebuild pulls four date-effective F&O entities and reconstructs the
   * establishment at every date on the ladder — minutes, not seconds, and longer
   * than the gateway's 300s upstream ceiling. So the request STARTS the run and
   * returns; this polls the status route until it settles and then reloads the
   * page data. Holding the request open would report a false failure on a run
   * that is still going perfectly well.
   */
  const doSync = useCallback(async () => {
    if (!canSync || syncing) return;
    setSyncing(true);
    setSyncNote('Rebuild started — reconstructing the establishment from F&O. This takes a few minutes.');
    try {
      await workforceApi.startSync();
    } catch (e: any) {
      setSyncing(false);
      // 409 is not a failure: someone else's rebuild is already running, and a
      // second concurrent pull of the same entities would buy nothing.
      const conflict = e?.response?.status === 409;
      setSyncNote(
        conflict
          ? (e?.response?.data?.detail as string) ?? 'A rebuild is already running.'
          : null,
      );
      toast[conflict ? 'error' : 'error'](
        conflict
          ? 'A rebuild is already running — wait for it to finish.'
          : 'Could not start the rebuild. You may not have permission, or the integration service is down.',
      );
      return;
    }

    // Poll until the run settles. Bounded: a rebuild that has not finished in
    // fifteen minutes has gone wrong, and the page says so rather than spinning
    // for the rest of the session.
    const started = Date.now();
    const LIMIT_MS = 15 * 60 * 1000;
    const tick = async (): Promise<void> => {
      if (Date.now() - started > LIMIT_MS) {
        setSyncing(false);
        setSyncNote(
          'The rebuild has been running for over fifteen minutes. It may still finish — reload the page shortly, or check the integration service scheduler status.',
        );
        return;
      }
      await new Promise((r) => setTimeout(r, 10000));
      try {
        const status = await workforceApi.getSyncStatus();
        if (status.manual?.running) {
          setSyncNote(
            `Rebuild running — started ${fmtSyncStamp(status.manual.started_at) ?? 'just now'} SAST.`,
          );
          return tick();
        }
        setSyncing(false);
        if (status.manual?.ok === false) {
          setSyncNote(`Rebuild failed: ${status.manual.error ?? 'unknown error'}`);
          toast.error('The rebuild failed. The figures on this page are unchanged.');
          return;
        }
        setSyncNote(
          `Rebuild finished in ${status.manual?.duration_seconds ?? '—'}s. Figures below are refreshed.`,
        );
        toast.success('Frames rebuilt — the page is showing the new figures.');
        // Re-fetch everything, including /periods: a rebuild writes a new live
        // frame for today, so the period list itself has changed.
        setReloadToken((t) => t + 1);
      } catch (e) {
        setSyncing(false);
        setSyncNote(
          'Lost track of the rebuild — it may still be running. Reload the page in a few minutes.',
        );
      }
    };
    void tick();
  }, [canSync, syncing]);

  // ---- range vs single date ------------------------------------------------
  // Resolved to indices against the CURRENT period list, so a stored date that
  // no longer exists degrades to the nearest end instead of filtering the chart
  // by a frame the slicer is not showing.
  const rangeStart = useMemo(() => {
    if (!periods.length) return 0;
    const i = periods.findIndex((p) => p.as_at === rangeFrom);
    return i < 0 ? 0 : i;
  }, [periods, rangeFrom]);
  const rangeEnd = useMemo(() => {
    if (!periods.length) return 0;
    const i = periods.findIndex((p) => p.as_at === rangeTo);
    return i < 0 ? periods.length - 1 : Math.max(i, rangeStart);
  }, [periods, rangeTo, rangeStart]);
  const rangeFromDate = periods[rangeStart]?.as_at ?? null;
  const rangeToDate = periods[rangeEnd]?.as_at ?? null;
  const asAtIndex = useMemo(() => periods.findIndex((p) => p.as_at === asAt), [periods, asAt]);

  /** Narrowing the range past the selected date drags the date with it. */
  const handleRange = useCallback(
    (start: number, end: number) => {
      const from = periods[start]?.as_at ?? null;
      const to = periods[end]?.as_at ?? null;
      if (!from || !to) return;
      setRangeFrom(from);
      setRangeTo(to);
      if (!asAt || (asAt >= from && asAt <= to)) {
        setPickerNote(null);
        return;
      }
      const moved = asAt < from ? periods[start] : periods[end];
      const wasLabel = frameLabel(periods.find((p) => p.as_at === asAt));
      setAsAt(moved.as_at);
      setPickerNote(
        `“As at” moved from ${wasLabel} to ${frameLabel(moved)} — ${wasLabel} now sits outside the range, ` +
          'and every point-in-time figure has been recomputed at the new date.',
      );
    },
    [periods, asAt],
  );

  /** …and picking a date outside the range widens the range to reach it. */
  const handleAsAt = useCallback(
    (next: string) => {
      setAsAt(next);
      const i = periods.findIndex((p) => p.as_at === next);
      if (i < 0) {
        setPickerNote(null);
        return;
      }
      if (i < rangeStart || i > rangeEnd) {
        const s = Math.min(i, rangeStart);
        const e = Math.max(i, rangeEnd);
        setRangeFrom(periods[s].as_at);
        setRangeTo(periods[e].as_at);
        setPickerNote(
          `Range widened to ${frameLabel(periods[s])} – ${frameLabel(periods[e])} so the date you picked is on the chart.`,
        );
      } else {
        setPickerNote(null);
      }
    },
    [periods, rangeStart, rangeEnd],
  );

  /** The trend is the one surface the range drives. Everything else is the date. */
  const rangedTrend = useMemo<WorkforceTrend | null>(() => {
    if (!trend) return null;
    if (!rangeFromDate || !rangeToDate) return trend;
    return {
      ...trend,
      series: (trend.series ?? []).map((s) => ({
        ...s,
        points: (s.points ?? []).filter((p) => p.as_at >= rangeFromDate && p.as_at <= rangeToDate),
      })),
    };
  }, [trend, rangeFromDate, rangeToDate]);

  // ---- derived -------------------------------------------------------------
  const selectedPeriod = useMemo(
    () => periods.find((p) => p.as_at === asAt) ?? null,
    [periods, asAt],
  );
  const periodLabel = selectedPeriod
    ? selectedPeriod.is_live
      ? 'Today'
      : selectedPeriod.label
    : fmtDay(asAt);
  const livePeriod = useMemo(() => periods.find((p) => p.is_live) ?? null, [periods]);
  const syncStamp = fmtSyncStamp(livePeriod?.source_sync_at);
  /** Which measures are stale, in words, FROM THE DATA, and how old the oldest of
   *  them is — the panel CONTENT, as distinct from panels_as_at, the frame it
   *  rides on, which moves every night whether or not it was recomputed. This
   *  banner used to carry a hand-written list that named six measures when twenty
   *  were stale, and a hardcoded list turns false in both directions the moment any
   *  block starts refreshing nightly.
   *
   *  A payroll month is not stale for being dated at its month's end: July's payroll
   *  is the newest there is until Finance closes August. Identity applies that rule
   *  to a block stamped `payroll_month`; a copy stored before kinds were stamped
   *  still reaches its list, so the same rule is applied here. The oldest date and
   *  age are then read from the blocks that remain, because identity's
   *  panels_stale_days / panels_computed_for may describe a payroll month this
   *  banner no longer lists. Labels are deduplicated because span arrives as two
   *  blocks. */
  const staleness = useMemo(() => {
    const provenance = panels?.panels_provenance ?? {};
    const blocks = (panels?.panels_stale_blocks ?? []).filter((b) => {
      const entry = provenance[b];
      const payrollMonth = entry?.kind === 'payroll_month' || PAYROLL_MONTH_BLOCKS.has(b);
      return !payrollMonth || (entry?.stale_days ?? 0) > PAYROLL_MONTH_STALE_AFTER_DAYS;
    });
    let days: number | null = null;
    let computedFor: string | null = null;
    for (const b of blocks) {
      const entry = provenance[b];
      if (typeof entry?.stale_days === 'number' && (days === null || entry.stale_days > days)) {
        days = entry.stale_days;
        computedFor = entry.computed_for;
      }
    }
    const seen = new Set<string>();
    const labels = blocks
      .map((b) => STALE_BLOCK_LABELS[b] ?? b.replace(/_/g, ' '))
      .filter((label) => (seen.has(label) ? false : (seen.add(label), true)));
    return { labels, days, computedFor };
  }, [panels]);

  // ---- render --------------------------------------------------------------
  if (!canView) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <Card>
          <CardContent className="flex items-start gap-3 p-6">
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
            <div>
              <h1 className="font-serif text-xl text-heritage-blue">Workforce Explorer</h1>
              <p className="mt-1 text-sm text-slate-500">
                You do not have access to this dashboard. It needs the Workforce Explorer permission,
                or one of the organisation console roles. Ask an administrator to grant
                <code className="mx-1 rounded bg-gray-100 px-1 py-0.5 text-[11px]">workforce:explorer</code>
                on your user record.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    // TooltipProvider is NOT mounted at app root — any Radix tooltip outside one
    // no-ops silently. The page owns its own so the subtree is always covered.
    <TooltipProvider>
      <div className="mx-auto max-w-[1180px] space-y-4 p-6">
        {/* ── header ──────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500">
              Impact &amp; Growth Platform
            </div>
            <h1 className="font-serif text-3xl leading-tight text-heritage-blue">Workforce Explorer</h1>
            <p className="mt-1 max-w-[62ch] text-sm text-slate-500">
              Establishment, headcount and workforce mix on one of three bases, at any published
              snapshot date. Every figure is rebuilt by the same point-in-time engine that produces
              the board pack — aggregates only, no person-level rows.
            </p>
            {syncStamp ? (
              <span className="mt-2.5 inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1 text-[11.5px] text-gray-600">
                <span className="inline-block h-[7px] w-[7px] rounded-full bg-emerald-500" />
                Data as at the last F&amp;O sync — {syncStamp} SAST
              </span>
            ) : null}
          </div>

          <div className="flex shrink-0 items-center gap-2">
          {canSync ? (
            <Button
              variant="outline"
              size="sm"
              onClick={doSync}
              disabled={syncing || booting}
              title="Rebuild the snapshot frames from F&O now, rather than waiting for the 07:00 SAST job. Takes a few minutes."
            >
              {syncing ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <History className="mr-1.5 h-3.5 w-3.5" />
              )}
              {syncing ? 'Syncing…' : 'Sync now'}
            </Button>
          ) : null}
          {canExport ? (
            <Button
              variant="outline"
              size="sm"
              onClick={doExport}
              disabled={exporting || !asAt}
              title="Download the current basis and period as a multi-sheet workbook (summary, trend, breakdown, panels, definitions and caveats)"
            >
              {exporting ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="mr-1.5 h-3.5 w-3.5" />
              )}
              Export workbook
            </Button>
          ) : null}
          </div>
        </div>

        {/* The rebuild narrates itself: it runs for minutes, and a button that
            goes quiet for that long reads as broken. */}
        {syncNote ? (
          <div className="flex items-start gap-2 rounded border border-blue-200 bg-blue-50 p-2.5 text-[11.5px] leading-snug text-blue-900">
            {syncing ? (
              <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin" />
            ) : (
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            )}
            <span>{syncNote}</span>
          </div>
        ) : null}

        {/* ── controls ────────────────────────────────────────────────── */}
        {/* Two time controls, and they are NOT interchangeable: the range picks
            which frames the time-series draws, the chips pick the one date every
            point-in-time figure is computed at. They are separated and captioned
            rather than merged, because merging them would mean either the chart
            or the tiles quietly answering a question nobody asked. */}
        <Card className="sticky top-0 z-10">
          <CardContent className="space-y-3 p-3">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
              <BasisToggle value={basis} onChange={setBasis} def={def} disabled={booting} />
              <div className="ml-auto flex items-center gap-2">
                {loading || breakdownLoading ? (
                  <span className="text-[11px] text-gray-400">Recomputing…</span>
                ) : null}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setReloadToken((t) => t + 1)}
                  disabled={booting || loading}
                >
                  <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', loading && 'animate-spin')} />
                  Refresh
                </Button>
              </div>
            </div>

            {periods.length ? (
              <div className="grid gap-x-6 gap-y-4 border-t border-gray-100 pt-3 lg:grid-cols-[minmax(240px,300px)_minmax(0,1fr)]">
                <div>
                  <RangeSlicer
                    periods={periods}
                    startIndex={rangeStart}
                    endIndex={rangeEnd}
                    onChange={handleRange}
                    asAtIndex={asAtIndex >= 0 ? asAtIndex : null}
                    def={def}
                    disabled={booting}
                  />
                  <p className="mt-1 text-[10.5px] leading-snug text-gray-500">
                    Chooses which snapshot frames the <b className="font-semibold">trend chart</b>{' '}
                    draws. It recomputes nothing — payroll keeps its own monthly window.
                  </p>
                </div>
                {/* [&>div>span]: PeriodChips lays its "As at" label out as a
                    flex sibling of the chip row; in a narrow column the label
                    itself shrinks and breaks to "AS / AT". The component is not
                    ours to change, so the column stops the label shrinking. */}
                <div className="[&>div>span]:shrink-0">
                  <PeriodChips
                    periods={periods}
                    value={asAt}
                    onChange={handleAsAt}
                    def={def}
                    disabled={booting}
                  />
                  <p className="mt-1.5 text-[10.5px] leading-snug text-gray-500">
                    The single date every <b className="font-semibold">point-in-time</b> figure is
                    computed at — the tiles, composition, breakdown, and the dashed marker on the
                    trend. It is always kept inside the range.
                  </p>
                </div>
              </div>
            ) : null}

            {pickerNote ? (
              <div className="flex items-start gap-1.5 rounded border border-gray-200 bg-gray-50 px-2 py-1.5 text-[11px] leading-snug text-gray-600">
                <Info className="mt-[1px] h-3.5 w-3.5 shrink-0 text-gray-400" />
                <span>{pickerNote}</span>
              </div>
            ) : null}
          </CardContent>
        </Card>

        {bootFailed ? (
          <div className="flex items-start gap-2 rounded border border-red-200 bg-red-50 p-2.5 text-xs text-red-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <strong>Snapshot periods could not be loaded.</strong> Without them there is no date to
              report on, so nothing below can be computed. Refresh to try again — if it persists, no
              snapshot has been published yet.
            </div>
          </div>
        ) : null}

        {!booting && !bootFailed && !periods.length ? (
          <div className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <strong>No workforce snapshots have been published yet.</strong> This page reads
              snapshots prepared in advance; until the first one is ready there is nothing to show.
              No figures are being estimated in the meantime.
            </div>
          </div>
        ) : null}

        {/* ── published vs rebuilt ────────────────────────────────────── */}
        {/* F&O is date-effective but NOT append-only: a retirement can be dated
            before a snapshot that has already been issued. The September budget
            alignment retired 131 seats with a 19 August date, so re-reading
            20 August today returns a smaller establishment than the papers show.
            Neither figure is wrong. Published figures are frozen and BOTH are
            shown — silently moving a number the board is holding is how people
            learn to trust neither copy. */}
        {summary?.published_drift && Object.keys(summary.published_drift).length ? (
          <div className="rounded border border-amber-200 bg-amber-50 p-3">
            <div className="flex items-start gap-2">
              <History className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
              <div className="min-w-0 flex-1">
                <div className="text-[12px] font-semibold text-amber-900">
                  This date no longer reproduces what was published
                  <Def def={def} k="published_baseline" />
                </div>
                <p className="mt-0.5 text-[11.5px] leading-snug text-amber-800">
                  {summary.published?.source
                    ? `${summary.published.source} is frozen below. `
                    : ''}
                  Retirements dated before this snapshot have been actioned since it was
                  issued, so re-reading the same date on today’s F&amp;O returns different
                  figures. The published numbers are never restated.
                </p>
                <table className="mt-2 w-full max-w-[520px] text-[11.5px]">
                  <thead>
                    <tr className="text-[10px] font-semibold uppercase tracking-wide text-amber-900/70">
                      <th className="py-0.5 pr-2 text-left font-semibold">Measure</th>
                      <th className="py-0.5 text-right font-semibold">As published</th>
                      <th className="py-0.5 text-right font-semibold">Re-read today</th>
                      <th className="py-0.5 pl-2 text-right font-semibold">Change</th>
                    </tr>
                  </thead>
                  <tbody className="text-amber-900">
                    {orderedDrift(summary.published_drift).map(([key, row]) => (
                      <tr key={key} className="border-t border-amber-200/70">
                        <td className="py-0.5 pr-2">{PUBLISHED_LABELS[key] ?? key}</td>
                        <td className="py-0.5 text-right font-semibold tabular-nums">
                          {fmtNum(row.published)}
                        </td>
                        <td className="py-0.5 text-right tabular-nums">{fmtNum(row.rebuilt)}</td>
                        <td className="py-0.5 pl-2 text-right tabular-nums">
                          {row.delta > 0 ? `+${fmtNum(row.delta)}` : fmtNum(row.delta)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : null}

        {/* ── KPI tiles ───────────────────────────────────────────────── */}
        {booting || (loading && !summary) ? (
          <TileRow>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-[86px] animate-pulse rounded-xl bg-gray-100" />
            ))}
          </TileRow>
        ) : summary?.tiles?.length ? (
          <TileRow>
            {summary.tiles.map((t) => (
              <MetricTile
                key={t.key}
                label={t.label}
                value={typeof t.value === 'number' ? fmtNum(t.value) : (t.value ?? '—')}
                sub={t.sub}
                defKey={t.key}
                def={def}
                definition={t.definition}
              />
            ))}
          </TileRow>
        ) : periods.length ? (
          <div className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <strong>Headline figures could not be loaded</strong> for {basisLabel(basis)} at{' '}
              {periodLabel}. The panels below are unaffected.
            </div>
          </div>
        ) : null}

        {/* ── trend ───────────────────────────────────────────────────── */}
        <TrendPanel
          trend={rangedTrend}
          summary={summary}
          basis={basis}
          asAt={asAt}
          periodLabel={periodLabel}
          framesTotal={periods.length}
          loading={loading}
          def={def}
        />

        {/* ── the vacancy, on the board's definition ──────────────────── */}
        {/* Sits directly under the trend and ABOVE the mix panels, because
            "approved less filled" is the first number a reader reaches for and
            it is not the vacancy. The step-down is shown in full so the two can
            be reconciled on the page rather than in a meeting. It also ages those
            vacancies: the separate vacancy-ageing panel aged a different population
            under the same word and is retired — identity no longer serves it. */}
        <VacancyStepDownPanel
          vacancy={summary?.vacancy}
          periodLabel={periodLabel}
          isLive={!!selectedPeriod?.is_live}
          loading={loading}
          def={def}
        />

        {/* ── composition + breakdown ─────────────────────────────────── */}
        <div className="grid gap-4 lg:grid-cols-2">
          <CompositionPanel
            summary={summary}
            basis={basis}
            periodLabel={periodLabel}
            loading={loading}
            def={def}
          />
          <BreakdownPanel
            breakdown={breakdown}
            groupBy={groupBy}
            onGroupBy={setGroupBy}
            periodLabel={periodLabel}
            basisNote={basisLabel(basis)}
            loading={breakdownLoading}
            def={def}
          />
        </div>

        {/* ── deeper measures ─────────────────────────────────────────── */}
        <div className="flex flex-wrap items-baseline justify-between gap-2 pt-2">
          <h2 className="flex items-center font-serif text-xl text-heritage-blue">
            Deeper measures
            <Def
              def={def}
              k="__deeper__"
              text="These panels do not all follow the controls above. Some are a point in time, some cover a window of months, the review measures follow the review cycle, and the Finance measures name the payroll month they come from. Each one states its own date."
            />
          </h2>
          <span className="text-[11px] text-gray-500">
            Each panel states its own window and basis — several deliberately do not follow the
            period picker.
          </span>
        </div>

        {/* Some blocks are handed forward onto each new live frame WITHOUT being
            recomputed, so the frame date they carry advances while the numbers
            inside do not. Said plainly, because a six-week-old figure sitting
            beside a headcount rebuilt this morning otherwise reads as today's.
            A payroll month is never listed merely for being its month — see
            `staleness`. */}
        {staleness.days !== null && staleness.days > 7 ? (
          <div className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-2.5 text-[11.5px] leading-snug text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <strong>
                {staleness.labels.length === 1
                  ? 'One measure was not recalculated for this date'
                  : `${staleness.labels.length} measures were not recalculated for this date`}
              </strong>{' '}
              — the oldest was last calculated for {fmtDay(staleness.computedFor)},{' '}
              {staleness.days} days before {periodLabel}: {staleness.labels.slice(0, 6).join(', ')}
              {staleness.labels.length > 6 ? ', and others' : ''}. The establishment above (the
              tiles, trend, composition and vacancies) is rebuilt every night. Each panel states its
              own date — read it on that date, not as today's.
            </div>
          </div>
        ) : null}

        {/* No blanket date banner here. It used to stamp one frame date on
            turnover, span, payroll and the student ratio, which was true of at
            most one of them. Every panel dates itself from its own provenance
            entry (panelDateLabel): a point in time, a window, a review cycle or a
            payroll month. */}

        <div className="grid gap-4 lg:grid-cols-2">
          <TurnoverPanel panels={panels} basis={basis} loading={loading} def={def} />
          <SpanPanel panels={panels} basis={basis} loading={loading} def={def} selectedAsAt={asAt} />
        </div>

        {/* ── Finance payroll months: who was paid, what the staff cost, and
               the student ratio ─────────────────────────────────────────── */}
        <PayrollPanel panels={panels} loading={loading} def={def} />
        <StaffCostPanel panels={panels} loading={loading} def={def} />
        <RatioPanel panels={panels} loading={loading} def={def} />

        {/* ── establishment: where the positions are, and what they hold ── */}
        <CampusFootprintPanel panels={panels} basis={basis} loading={loading} def={def} />

        {/* ── roles: how each role is actually staffed, then the catalogue ─ */}
        {/* The catalogue runs full width: titles by function, four tiles and two
            charts are too tall for a half-width card. */}
        <RoleContractMixPanel panels={panels} basis={basis} loading={loading} def={def} />
        <RoleCataloguePanel panels={panels} basis={basis} loading={loading} def={def} />

        {/* ── organisation shape: the academic model, and its single points
               of failure ─────────────────────────────────────────────────── */}
        <IcaGovernancePanel panels={panels} basis={basis} loading={loading} def={def} />
        <KeyPersonPanel panels={panels} basis={basis} loading={loading} def={def} />

        {/* ── who the workforce is ─────────────────────────────────────── */}
        <EquityPanel panels={panels} basis={basis} loading={loading} def={def} />

        {/* SessionalPanel carries the payroll month's "Paid in the month" section
            itself (sessional_capacity) — no second panel is mounted for it. */}
        <div className="grid gap-4 lg:grid-cols-2">
          <AgeServicePanel panels={panels} basis={basis} loading={loading} def={def} />
          <SessionalPanel panels={panels} basis={basis} loading={loading} def={def} />
        </div>

        {/* ── the platform: how far eligible employees have taken it up, then
               how they are rated — the pack's order, adoption before the
               indicators that count the same people ─────────────────────── */}
        <AdoptionPanel panels={panels} basis={basis} loading={loading} def={def} />
        <IndicatorsPanel panels={panels} basis={basis} loading={loading} def={def} />

        {/* ── how they move ───────────────────────────────────────────── */}
        <div className="grid gap-4 lg:grid-cols-2">
          <MobilityPanel panels={panels} basis={basis} loading={loading} def={def} />
          <ExitRolesPanel panels={panels} basis={basis} loading={loading} def={def} />
        </div>

        {/* ── footer ──────────────────────────────────────────────────── */}
        <p className="max-w-[80ch] pt-2 text-[11px] leading-relaxed text-gray-500">
          {budgetAligned === false
            ? 'Establishment figures are NOT budget-aligned on this snapshot set — read the approved-position counts with that in mind. '
            : 'Establishment figures use the budget-aligned rule. '}
          Snapshots are prepared in advance, not live reads; “Today” is the establishment at the last
          F&amp;O sync
          {syncStamp ? ` (${syncStamp} SAST)` : ''}
          {generatedAt ? `, snapshots prepared ${fmtSyncStamp(generatedAt)} SAST` : ''}. A figure that
          could reveal a group of fewer than five people is withheld to protect small groups, and
          marked as withheld rather than dropped. Aggregates only — no person-level data is served to
          this page.
        </p>
      </div>
    </TooltipProvider>
  );
};

export default WorkforceExplorerPage;
